/**
 * CARI @mention autocomplete.
 *
 * Dropdown for @mentions in the CARI chat input. Mirrors the TERRA
 * autocomplete pattern but calls CARI's endpoint and surfaces
 * Phreatic-specific entity types (site / asset / well / aquifer).
 *
 * Triggered by typing '@' in the textarea. Keyboard navigation: arrow
 * keys, Enter or Tab to select, Escape to close.
 */
import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';

interface MentionItem {
  id?: string;
  name: string;
  type: 'site' | 'asset' | 'well' | 'aquifer';
  site_id?: string | null;
}

const TYPE_ICONS: Record<string, string> = {
  site: '\u{1F4CD}',     // 📍
  asset: '\u{2699}\u{FE0F}',  // ⚙️
  well: '\u{1F535}',     // 🔵
  aquifer: '\u{1F30A}',  // 🌊
};

const TYPE_LABELS: Record<string, string> = {
  site: 'Site',
  asset: 'Pump',
  well: 'Well',
  aquifer: 'Aquifer',
};

interface Props {
  query: string;        // Text after the '@'
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  visible: boolean;
  scopeSiteId?: string | null;  // Optional site to scope well/asset searches
}

export interface CARIMentionHandle {
  handleKeyDown: (e: React.KeyboardEvent) => boolean;
}

export const CARIMentionAutocomplete = forwardRef<CARIMentionHandle, Props>(function CARIMentionAutocomplete(
  { query, onSelect, onClose, visible, scopeSiteId = null },
  ref,
) {
  const [items, setItems] = useState<MentionItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  // Debounced fetch — same 200ms delay as TERRA's component.
  useEffect(() => {
    if (!visible || !query) {
      setItems([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { apiClient } = await import('@/api/client');
        // Search all four CARI entity types in parallel and merge.
        const params = (type: string, limit: number) => ({
          q: query,
          type,
          limit,
          ...(scopeSiteId && (type === 'well' || type === 'asset') ? { site_id: scopeSiteId } : {}),
        });
        const [sites, assets, wells, aquifers] = await Promise.all([
          apiClient.get('/phreatic/cari/autocomplete', { params: params('site', 4) }).then((r) => r.data.items).catch(() => []),
          apiClient.get('/phreatic/cari/autocomplete', { params: params('asset', 3) }).then((r) => r.data.items).catch(() => []),
          apiClient.get('/phreatic/cari/autocomplete', { params: params('well', 3) }).then((r) => r.data.items).catch(() => []),
          apiClient.get('/phreatic/cari/autocomplete', { params: params('aquifer', 2) }).then((r) => r.data.items).catch(() => []),
        ]);
        setItems([...sites, ...assets, ...wells, ...aquifers]);
        setSelectedIndex(0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query, visible, scopeSiteId]);

  // Keyboard handler exposed for the parent textarea via the
  // useMentionKeyboard hook below. The component renders the dropdown UI
  // only; the parent decides whether to forward keystrokes.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!visible || items.length === 0) return false;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % items.length);
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + items.length) % items.length);
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        onSelect(items[selectedIndex]);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return true;
      }
      return false;
    },
    [visible, items, selectedIndex, onSelect, onClose],
  );

  // Expose the keyboard handler imperatively so the parent textarea can
  // forward keystrokes when the dropdown is visible.
  useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  if (!visible) return null;
  if (!loading && items.length === 0 && query.length > 0) return null;

  return (
    <div
      className="absolute bottom-full left-0 right-0 mb-1 overflow-hidden rounded-lg border border-cyan-500/30 bg-mono-800 shadow-xl z-50"
    >
      {loading && items.length === 0 && (
        <div className="px-3 py-2 text-xs text-mono-500">Searching…</div>
      )}
      {items.map((item, i) => (
        <button
          key={`${item.type}-${item.name}-${i}`}
          className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
            i === selectedIndex
              ? 'bg-cyan-500/10 text-mono-100'
              : 'text-mono-300 hover:bg-mono-700/40'
          }`}
          onClick={() => onSelect(item)}
          onMouseEnter={() => setSelectedIndex(i)}
        >
          <span className="text-sm">{TYPE_ICONS[item.type]}</span>
          <span className="flex-1 truncate">{item.name}</span>
          <span className="text-[10px] text-mono-500">{TYPE_LABELS[item.type]}</span>
        </button>
      ))}
    </div>
  );
});


/**
 * Hook — derive @mention state from the textarea value + cursor position.
 *
 * Returns ``{active, query, atIndex}`` where:
 *   active   — true when the cursor is INSIDE an in-progress mention
 *              (i.e. after a '@' that's at the start or preceded by
 *              whitespace, with no intervening whitespace)
 *   query    — the text typed after the '@'
 *   atIndex  — character index of the '@' so the caller can slice it out
 *              when replacing with the selected mention
 */
export function useCARIMentionState(value: string, cursorPos: number) {
  const beforeCursor = value.slice(0, cursorPos);
  const atIndex = beforeCursor.lastIndexOf('@');

  if (atIndex === -1) return { active: false, query: '', atIndex: -1 };

  const afterAt = beforeCursor.slice(atIndex + 1);
  if (afterAt.includes(' ') || afterAt.includes('\n')) {
    return { active: false, query: '', atIndex: -1 };
  }
  if (atIndex > 0 && !/\s/.test(beforeCursor[atIndex - 1])) {
    return { active: false, query: '', atIndex: -1 };
  }

  return { active: afterAt.length > 0, query: afterAt, atIndex };
}
